import { useState } from "react"
import { useMutation } from "urql"
import { pluralize } from "utils/stringUtils"

import type {
  CreateGoodsReceiptPalletMutation,
  GoodsReceiptMutationCreatePalletArgs,
  GoodsReceiptPallet,
  GoodsReceiptPalletCreateFailure,
  GoodsReceiptPalletCreateInput,
  GoodsReceiptPalletCreateSuccess,
} from "@/graphql/codegen/graphql"
import { CreateGoodsReceiptPalletDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import type { ActionOutcome } from "@/types/common"

interface UseCreateGoodsReceiptPalletsParams {
  onSuccess: () => void
}

interface UseCreateGoodsReceiptPalletsReturn {
  create: (input: GoodsReceiptPalletCreateInput[]) => Promise<ActionOutcome<GoodsReceiptPallet[]>>
  isCreating: boolean
}

export const useCreateGoodsReceiptPallets = ({
  onSuccess,
}: UseCreateGoodsReceiptPalletsParams): UseCreateGoodsReceiptPalletsReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [isCreating, setIsCreating] = useState(false)

  const [, executeMutation] = useMutation<CreateGoodsReceiptPalletMutation, GoodsReceiptMutationCreatePalletArgs>(
    CreateGoodsReceiptPalletDocument
  )

  const create = async (inputs: GoodsReceiptPalletCreateInput[]): Promise<ActionOutcome<GoodsReceiptPallet[]>> => {
    setIsCreating(true)

    const createdPallets: GoodsReceiptPallet[] = []
    let error: Error | undefined

    for (const input of inputs) {
      const { data, error: mutationError } = await executeMutation({ input })
      const output = data?.goodsReceipt.createPallet

      if (mutationError || output?.__typename === "GoodsReceiptPalletCreateFailure") {
        const errorMessage =
          mutationError?.message ?? (output as GoodsReceiptPalletCreateFailure).error ?? "Failed to create pallet"
        error = new Error(errorMessage)
        break
      }

      if (output?.__typename === "GoodsReceiptPalletCreateSuccess") {
        createdPallets.push((output as GoodsReceiptPalletCreateSuccess).goodsReceiptPallet)
      }
    }

    setIsCreating(false)

    const formattedPalletCount = `${createdPallets.length} ${pluralize(createdPallets.length, "pallet", "pallets")}`

    if (error && createdPallets.length > 0) {
      notify({
        type: NotificationType.Warning,
        message: `${formattedPalletCount} created, but failed to create remaining pallets: ${error.message}`,
      })
      onSuccess()
      return { data: createdPallets, error }
    }

    if (error) {
      notify({
        type: NotificationType.Error,
        message: error.message,
      })
      return { error }
    }

    notify({
      type: NotificationType.Success,
      content: `${formattedPalletCount} successfully created`,
    })
    onSuccess()
    return { data: createdPallets }
  }

  return {
    create,
    isCreating,
  }
}
